import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type LogEvent, type LogSearchResult, type SavedLogView } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner } from "@/components/ui";

export function ObservabilityLogsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<LogSearchResult | null>(null);
  const [severity, setSeverity] = useState("");
  const [service, setService] = useState("");
  const [q, setQ] = useState("");
  const [requestId, setRequestId] = useState("");
  const [selected, setSelected] = useState<LogEvent | null>(null);
  const [views, setViews] = useState<SavedLogView[]>([]);
  const [viewName, setViewName] = useState("");

  const currentFilters = () => ({
    severity: severity || undefined,
    service: service || undefined,
    q: q || undefined,
    request_id: requestId || undefined,
  });

  const loadViews = async () => {
    if (!id) return;
    try {
      const { data } = await api.listLogViews(id);
      setViews(data ?? []);
    } catch {
      /* views optional if migration pending */
    }
  };

  const load = async (cursor?: string) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.searchLogs(id, {
        ...currentFilters(),
        cursor,
        limit: 50,
      });
      if (cursor && result) {
        setResult({
          ...data,
          events: [...result.events, ...data.events],
        });
      } else {
        setResult(data);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    void loadViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const applyView = (v: SavedLogView) => {
    const f = (v.filters ?? {}) as Record<string, string>;
    setSeverity(f.severity ?? "");
    setService(f.service ?? "");
    setQ(f.q ?? "");
    setRequestId(f.request_id ?? "");
    // search after state updates — pass filters directly
    void (async () => {
      setLoading(true);
      try {
        const { data } = await api.searchLogs(id, {
          severity: f.severity || undefined,
          service: f.service || undefined,
          q: f.q || undefined,
          request_id: f.request_id || undefined,
          limit: 50,
        });
        setResult(data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Logs Explorer"
        description="Search structured request logs with filters, detail drawer, and CSV export."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="text-xs text-text-muted">
          Severity
          <select
            className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="">All</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
        </label>
        <label className="text-xs text-text-muted">
          Service
          <input
            className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="api_gateway"
          />
        </label>
        <label className="text-xs text-text-muted">
          Request ID
          <input
            className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 font-mono text-sm"
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
          />
        </label>
        <label className="min-w-[12rem] flex-1 text-xs text-text-muted">
          Contains
          <input
            className="mt-1 block w-full rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="route or message"
          />
        </label>
        <button type="button" className="btn-primary" onClick={() => void load()}>
          Search
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            void api
              .downloadLogsCSV(id, {
                severity: severity || undefined,
                service: service || undefined,
                q: q || undefined,
                request_id: requestId || undefined,
              })
              .catch((e) => setError(e))
          }
        >
          Export CSV
        </button>
      </div>

      <div className="card mb-4 p-3">
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <label className="text-xs text-text-muted">
            Save current filters as view
            <input
              className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="Errors only"
            />
          </label>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              void (async () => {
                try {
                  await api.saveLogView(id, viewName, {
                    severity: severity || undefined,
                    service: service || undefined,
                    q: q || undefined,
                    request_id: requestId || undefined,
                  });
                  setViewName("");
                  await loadViews();
                } catch (e) {
                  setError(e);
                }
              })()
            }
          >
            Save view
          </button>
        </div>
        {views.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-text-muted">Saved views:</span>
            {views.map((v) => (
              <button
                key={v.id}
                type="button"
                className="mono-chip hover:bg-primary/10"
                onClick={() => applyView(v)}
                title={JSON.stringify(v.filters)}
              >
                {v.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-muted">No saved views yet. Save the current filter set to reuse it.</p>
        )}
      </div>

      {loading && !result ? (
        <Spinner center label="Loading logs…" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="card overflow-hidden lg:col-span-2">
            <div className="max-h-[32rem] overflow-auto">
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Sev</th>
                    <th>Service</th>
                    <th>Message</th>
                    <th className="text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(result?.events ?? []).map((e) => (
                    <tr
                      key={e.event_id + e.created_at}
                      className="cursor-pointer hover:bg-surface-container-low/60"
                      onClick={() => setSelected(e)}
                    >
                      <td className="whitespace-nowrap text-xs text-text-muted">
                        {new Date(e.created_at).toLocaleTimeString()}
                      </td>
                      <td>
                        <span className="mono-chip">{e.severity}</span>
                      </td>
                      <td className="font-mono text-xs">{e.service}</td>
                      <td className="max-w-[240px] truncate font-mono text-xs">{e.event_message}</td>
                      <td className="text-right tabular-nums">{e.status_code || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result?.has_more ? (
              <div className="border-t border-border-standard p-3">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void load(result.next_cursor)}
                >
                  Load more
                </button>
              </div>
            ) : null}
            {!result?.events?.length ? (
              <p className="p-4 text-sm text-text-muted">No log events in this range yet.</p>
            ) : null}
          </div>

          <div className="card p-4">
            <h3 className="mb-2 font-sans text-sm font-semibold text-text-primary">Event detail</h3>
            {!selected ? (
              <p className="text-sm text-text-muted">Select a row to inspect fields.</p>
            ) : (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs text-text-disabled">Message</dt>
                  <dd className="font-mono text-xs">{selected.event_message}</dd>
                </div>
                <div>
                  <dt className="text-xs text-text-disabled">Request ID</dt>
                  <dd className="font-mono text-xs">{selected.request_id || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-text-disabled">Route</dt>
                  <dd className="font-mono text-xs">
                    {selected.method} {selected.route_template}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-text-disabled">Error</dt>
                  <dd className="font-mono text-xs">{selected.error_code || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-text-disabled">Duration</dt>
                  <dd className="tabular-nums">{selected.duration_ms}ms</dd>
                </div>
                <div>
                  <dt className="text-xs text-text-disabled">Redaction</dt>
                  <dd>v{selected.redaction_version}</dd>
                </div>
                <pre className="mt-3 max-h-48 overflow-auto rounded bg-surface-container-low p-2 font-mono text-[10px]">
                  {JSON.stringify(selected, null, 2)}
                </pre>
              </dl>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
